/**
 * CD R11 Finding #4 — Dark-spot auto-callout detector.
 *
 * "When a brief touches one of the 9 non-disseminator countries (DPRK,
 * Eritrea, Syria…), the defence pack MUST auto-surface 'this region is a
 * surveillance dark spot — absence of signal is not absence of disease.'
 * This is the single most important honesty claim in the whole product."
 *
 * This module is the PURE logic that powers the §7.25 Minister Briefing
 * Pack's auto-callout — separated from the React surface so it's CI-gateable
 * without a DOM. The surface (MinisterBriefingPack.tsx → DefencePack)
 * imports `detectDarkSpots()` and renders an `<HonestyRail variant="limitation"
 * label="DARK SPOT">` panel per matched non-disseminator.
 *
 * Detection rules (in priority order, first hit wins per pin):
 *   1. `pin.context.iso3` matches one of the 9 ISO3 codes (PRK / ERI / TKM /
 *      YEM / SYR / SOM / SSD / LBY / MMR) — exact uppercase match. This is
 *      the canonical channel — every surface that pins a region-scoped item
 *      SHOULD populate context.iso3.
 *   2. `pin.context.regionId` of the form `ISO3:<iso3>` — fallback for the
 *      few surfaces that key off the region register's composite id rather
 *      than a bare iso3.
 *   3. `pin.label` contains the non-disseminator's country name as a
 *      substring (case-insensitive) — last-resort fallback so a free-text
 *      pin like "DPRK respiratory cluster" still trips the callout. Match
 *      strings are deliberately specific (e.g. "Myanmar", not "Burma" —
 *      the register canonical name is "Myanmar (post-2021 coup)").
 *
 * The single source of truth for the 9-country list is
 * `loadCoverageTier().nonDisseminators` from
 * @biomax/plugin-surveillance-feed. NEVER hard-code the list here — when a
 * regime changes, only the register updates and this detector picks it up
 * automatically.
 */

import { loadCoverageTier, type NonDisseminator } from '@biomax/plugin-surveillance-feed';
import type { PinnedItem } from './pin-to-brief';

/** A pinned item that has been matched to a non-disseminator country.
 *  The surface renders one HonestyRail per dedupe'd entry. */
export interface DarkSpotMatch {
  /** The pin that triggered the match. */
  pin: PinnedItem;
  /** The non-disseminator register entry the pin matched. */
  country: NonDisseminator;
  /** Which channel triggered the match — useful for the rendered "why" text
   *  and for the test to assert each detection rule fires. */
  matchedVia: 'iso3' | 'regionId' | 'label';
}

const NAME_TOKENS_FOR_LABEL_MATCH: Readonly<Record<string, readonly string[]>> = {
  PRK: ['DPRK', 'North Korea'],
  ERI: ['Eritrea'],
  TKM: ['Turkmenistan'],
  YEM: ['Yemen'],
  SYR: ['Syria'],
  SOM: ['Somalia'],
  SSD: ['South Sudan'],
  LBY: ['Libya'],
  MMR: ['Myanmar'],
};

/** Inspect a single pin against the non-disseminator register; returns the
 *  first matched country (priority: iso3 → regionId → label substring), or
 *  null if the pin doesn't touch any of the 9. */
function matchOnePin(
  pin: PinnedItem,
  byIso3: ReadonlyMap<string, NonDisseminator>,
): DarkSpotMatch | null {
  const ctx = pin.context ?? {};

  // Rule 1: context.iso3
  if (typeof ctx['iso3'] === 'string') {
    const hit = byIso3.get(ctx['iso3'].toUpperCase());
    if (hit) return { pin, country: hit, matchedVia: 'iso3' };
  }

  // Rule 2: context.regionId of the form ISO3:<iso3>
  if (typeof ctx['regionId'] === 'string') {
    const m = ctx['regionId'].match(/^ISO3:([A-Z]{3})$/i);
    if (m) {
      const hit = byIso3.get(m[1]!.toUpperCase());
      if (hit) return { pin, country: hit, matchedVia: 'regionId' };
    }
  }

  // Rule 3: label substring match — case-insensitive against
  // canonical NAME_TOKENS_FOR_LABEL_MATCH entries
  if (pin.label) {
    const haystack = pin.label.toLowerCase();
    for (const [iso3, tokens] of Object.entries(NAME_TOKENS_FOR_LABEL_MATCH)) {
      for (const token of tokens) {
        if (haystack.includes(token.toLowerCase())) {
          const country = byIso3.get(iso3);
          if (country) return { pin, country, matchedVia: 'label' };
        }
      }
    }
  }

  return null;
}

/** Examine the operator's pinned-items queue and return one DarkSpotMatch
 *  per pin that touches a non-disseminator country.
 *
 *  Suppressed pins are skipped (operator explicitly excluded them from the
 *  pack — matches the bluf-draft suppression contract).
 *
 *  Same country matched twice via different pins → both returned (the
 *  surface dedupes by country.iso3 before rendering a single callout per
 *  country, see MinisterBriefingPack.tsx). Returning per-pin keeps this
 *  function pure + auditable.
 *
 *  Empty input → empty output. No pins matching → empty output. The surface
 *  treats empty as "no callout" (silently absent — no false-positive
 *  callout when the brief doesn't touch a dark spot). */
export function detectDarkSpots(pinned: readonly PinnedItem[]): readonly DarkSpotMatch[] {
  const reg = loadCoverageTier();
  const byIso3 = new Map<string, NonDisseminator>(
    reg.nonDisseminators.map(c => [c.iso3.toUpperCase(), c]),
  );
  const matches: DarkSpotMatch[] = [];
  for (const pin of pinned) {
    if (pin.suppressed) continue;
    const hit = matchOnePin(pin, byIso3);
    if (hit) matches.push(hit);
  }
  return matches;
}

/** Dedupe matches by country.iso3 (keeps first match per country). The
 *  surface uses this so each non-disseminator gets ONE callout panel even
 *  when multiple pins touch the same country. */
export function dedupeMatchesByCountry(
  matches: readonly DarkSpotMatch[],
): readonly DarkSpotMatch[] {
  const seen = new Set<string>();
  const out: DarkSpotMatch[] = [];
  for (const m of matches) {
    const key = m.country.iso3.toUpperCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(m);
  }
  return out;
}
